import Axios from "axios";
import { toast } from "react-toastify";

import AppData from "@app@::@data";

let axiosRateLimitInterceptorInitialized = false;
let csrfTokenCache: string | null = null;

if (!axiosRateLimitInterceptorInitialized) {
    axiosRateLimitInterceptorInitialized = true;

    Axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status;

            if (status === 429 && !toast.isActive("rate-limit-notification")) {
                const retryAfter = error?.response?.headers?.["retry-after"];
                const retryMessage = retryAfter ? ` Please retry in ${retryAfter}s.` : " Please retry shortly.";
                toast(`You're being rate limited.${retryMessage}`, { type: "warning", autoClose: 5000, toastId: "rate-limit-notification" });
            }

            return Promise.reject(error);
        },
    );
}

const testUp = () => Axios.get(`${AppData.project.backendDomain}/api/up`);

const getUserData = () => Axios.get(`${AppData.project.backendDomain}/api/auth`, { withCredentials: true });

const getCsrfToken = () => Axios.get(`${AppData.project.backendDomain}/api/auth/csrf-token`, { withCredentials: true });

const resolveCsrfToken = async (): Promise<string | null> => {
    if (csrfTokenCache) return csrfTokenCache;

    try {
        const response = await getCsrfToken();
        const csrfToken = response?.data?.csrfToken;

        if (typeof csrfToken === "string" && csrfToken.length > 0) {
            csrfTokenCache = csrfToken;
            return csrfTokenCache;
        }
    } catch {}

    return null;
};

const logout = async (csrfToken?: string) => {
    const token = csrfToken || (await resolveCsrfToken());

    try {
        return await Axios.post(`${AppData.project.backendDomain}/api/auth/logout`, {}, { withCredentials: true, headers: token ? { "x-csrf-token": token } : {} });
    } finally {
        csrfTokenCache = null;
    }
};

const getGuilds = () => Axios.get(`${AppData.project.backendDomain}/api/discord/guilds`, { withCredentials: true });

const getGuild = (id: Snowflake) => Axios.get(`${AppData.project.backendDomain}/api/discord/guilds/${id}`, { withCredentials: true });

const getGuildConfig = (id: Snowflake) => Axios.get(`${AppData.project.backendDomain}/api/discord/guilds/${id}/config`, { withCredentials: true });

const resetGuildConfig = (id: Snowflake) => Axios.delete(`${AppData.project.backendDomain}/api/discord/guilds/${id}/reset`, { withCredentials: true });

const postCustomer = async (data: any, path: string) => {
    const csrfToken = await resolveCsrfToken();

    return Axios.post(`${AppData.project.backendDomain}/api/customer/${path}`, { ...data }, { withCredentials: true, headers: csrfToken ? { "x-csrf-token": csrfToken } : {} });
};

const getWordDefinition = (word: string) => Axios.get(`https://api.dictionaryapi.dev/api/v2/entries/en/${word.toLowerCase()}`);

const list = { testUp, getUserData, getCsrfToken, logout, getGuilds, getGuild, getGuildConfig, resetGuildConfig, postCustomer, getWordDefinition };

export default list;
